import React, { useState, useCallback } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { IconFileDownload, IconUpload, IconFileSpreadsheet, IconX } from '@tabler/icons-react';
import { useDownloadBookingTemplate } from '../../../../services/booking/hooks/v4/useDownloadBookingTemplate';
import { useImportBooking } from '../../../../services/booking/hooks/v4/useImportBooking';

// Tipe props untuk komponen ini
interface ImportBookingDialogProps {
    show: boolean;
    onClose: () => void;
    propertyId: string;
    // Kita akan menambahkan fungsi onImportSuccess nanti
    // onImportSuccess: () => void; 
}

const ImportBookingDialog: React.FC<ImportBookingDialogProps> = ({ show, onClose, propertyId }) => {
    // State untuk file yang dipilih
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const { mutate: downloadTemplate, isPending: isDownloading } = useDownloadBookingTemplate();
    const { mutate: importBooking, isPending: isUploading } = useImportBooking();

    // Handler untuk perubahan input file
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    };

    // Handler untuk drag-and-drop
    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            setSelectedFile(event.dataTransfer.files[0]);
            // Bersihkan input agar bisa memilih file yang sama lagi
            event.dataTransfer.clearData();
        }
    }, []);

    const handleDownloadTemplate = () => {
        downloadTemplate(propertyId);
    };

    const handleUploadFile = () => {
        if (!selectedFile) return;
        importBooking({ propertyId, file: selectedFile }, {
            onSuccess: () => {
                // Tutup modal setelah berhasil
                handleClose();
            }
        });
    };

    const handleClose = () => {
        setSelectedFile(null);
        setIsDragOver(false);
        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Import Bookings</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Bagian 1: Download Template */}
                <div className="mb-4">
                    <h6 className="fw-bold">Step 1: Get the Template</h6>
                    <p className="text-muted small">
                        Download our Excel template to ensure your data is in the correct format for a successful import.
                    </p>
                    <Button variant="outline-primary" onClick={handleDownloadTemplate} disabled={isDownloading}>
                        {isDownloading ? (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                            <IconFileDownload size={16} className="me-2" />
                        )}
                        Download Template
                    </Button>
                </div>

                <hr />

                {/* Bagian 2: Upload File */}
                <div className="mt-4">
                    <h6 className="fw-bold">Step 2: Upload Your File</h6>
                    <p className="text-muted small">
                        Once you've filled out the template, upload the saved Excel file here.
                    </p>

                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`p-4 border rounded-3 text-center ${isDragOver ? 'border-primary bg-light' : 'border-dashed'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => document.getElementById('file-upload-input')?.click()}
                    >
                        <input
                            type="file"
                            id="file-upload-input"
                            className="d-none"
                            onChange={handleFileChange}
                            accept=".xlsx, .xls, .csv" // Batasi tipe file
                        />
                        <IconUpload size={48} className="text-muted mb-3" />
                        <p className="mb-1">
                            {isDragOver ? "Drop the file here!" : "Drag & drop your file here, or click to browse."}
                        </p>
                        <small className="text-muted">Supports: .xlsx, .xls, .csv</small>
                    </div>

                    {selectedFile && (
                        <Alert variant="success" className="d-flex align-items-center mt-3 p-2">
                            <IconFileSpreadsheet size={20} className="me-2" />
                            <span className="fw-bold flex-grow-1">{selectedFile.name}</span>
                            <Button variant="close" size="sm" onClick={() => setSelectedFile(null)} aria-label="Remove file">
                                <IconX size={16} />
                            </Button>
                        </Alert>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleUploadFile}
                    disabled={!selectedFile || isUploading}
                >
                    {isUploading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Importing...
                        </>
                    ) : (
                        <IconUpload size={16} className="me-2" />
                    )}
                    Upload & Import
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportBookingDialog;